import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { LinkableAccount } from '../../_models/fundraising/LinkableAccount';
import { PricingTier, Cue5050 } from '../../_models/fundraising/Cue5050';
import { DialogService } from '../../_services/dialog.service';
import { Cue5050Service } from '../../_services/fundraising/cue5050.service';
import { VendorService } from '../../_services/fundraising/vendor.service';
import { formatDateTime } from '../../_shared/utils';
import { AuthService } from '../../_services/auth.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-cue5050-create',
  templateUrl: './cue5050-create.component.html',
  styleUrls: ['./cue5050-create.component.css']
})
export class Cue5050CreateComponent implements OnInit, AfterContentChecked {

  public form: FormGroup;

  public formControls(name: string)  {
    return this.form.controls[name];
  }

  public eventFormControls(name: string)  {
    return (this.form.controls.event as FormGroup)?.controls[name];
  }

  public locationFormControls(name: string)  {
    return (this.eventFormControls('location') as FormGroup)?.controls[name];
  }

  public cue5050: Cue5050;
  public eventId: number;
  public vendorName: string;
  public defaultAdminPercent: number;

  public hasValidationErrors: boolean;
  public showAdvancedOptions: boolean;
  public showCustomizableWinnerText: boolean;

  public linkedMerchants: LinkableAccount[] = [];

  public cloning = false;
  public viewing = false;

  firstFiveTickets: string[] = [];

  readonly maxTicketNumber = 4294967295;

  constructor(
    private cue5050Service: Cue5050Service,
    private vendorService: VendorService,
    private authService: AuthService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges()
  }

  ngOnInit(): void {

    this.spinner.show();

    this.route.params
      .subscribe(params => {
        this.eventId = params['id'];

        this.cue5050Service.getById(this.eventId ?? 0).subscribe(
          data => {
            this.cue5050 = data;

            this.cue5050Service.getLinkedMerchants().subscribe(
              data => {
                this.linkedMerchants = data;

                if (!this.linkedMerchants || this.linkedMerchants.length < 1) {
                  this.dialogService
                    .alert(
                      'No Fundraising Partners Available',
                      'It looks like your vendor account currently has no charities or fundraiser beneficiaries tied to it. \
                        This may be because no partner invites have been sent, the charity has yet to accept your invite, \
                          or they have yet to finish their account set up. Please contact support@connectwithcue.com if you have any questions',
                      this.viewRef
                    )
                    .then(() => {
                      this.form.markAsPristine();
                      this.navigateUp();
                    });

                }
                this.route.queryParams
                  .subscribe(params => {
                    if (params.c == 'true'){
                      this.cloning = true;
                      this.eventId = 0;
                      this.cue5050.startingTicketNumber = 1;
                      this.cue5050.event.name += ' COPY';
                    }
                  })
                this.createForm(this.cue5050);
                this.spinner.hide();
              },
              error => {
                this.dialogService.alert("Oops something went wrong...", error.error, this.viewRef);
                this.spinner.hide();
              }
            );
          },
          error => {
            this.dialogService.alert("Unable to load cue5050", error.error, this.viewRef);
        });

        this.vendorService.getVendorAsCurrentUser()
          .subscribe(vendor => {
            this.defaultAdminPercent = vendor.vendor.defaultAdminPercent;
          })

      })

    }

  createForm(cue5050: Cue5050) {

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    this.form = this.fb.group({
      cue5050Id: [cue5050?.cue5050Id || 0, Validators.required],
      restrictState: [cue5050?.restrictState ?? false],
      restrictCity: [cue5050?.restrictCity ?? false],

      event: new FormGroup({
        name: new FormControl(cue5050?.event.name, Validators.required),
        eventId: new FormControl(cue5050?.event.eventId || 0, Validators.required),
        merchantId: new FormControl(cue5050?.event.merchantId || 0, [Validators.required, Validators.min(1)]),
        fundraisingGoal: new FormControl((cue5050?.event.fundraisingGoal || 0) / 100, [Validators.min(0), Validators.max(10000000)]),
        realEventPeriod: new FormControl({ value: { startDate: cue5050?.event.realStartDateTime ? new Date(cue5050?.event.realStartDateTime) : today, endDate: cue5050?.event.realEndDateTime ? new Date(cue5050?.event.realEndDateTime) : tomorrow, isNew: false }, disabled: false }, Validators.required),
        enableOnlineSales: new FormControl(cue5050?.event.enableOnlineSales ?? false, Validators.required)
      }),

      receiptInfo: new FormGroup({
        header1: new FormControl(cue5050?.receiptInfo?.header1 ?? 'Major Header', [Validators.required, Validators.minLength(1), Validators.maxLength(18)]),
        header2: new FormControl(cue5050?.receiptInfo?.header2 ?? 'Minor Header', [Validators.maxLength(18)]),
        lineItems: this.createLineItems(cue5050?.receiptInfo?.lineItems),
        subheader: new FormControl(cue5050?.receiptInfo?.subheader ?? 'Subheader', [Validators.maxLength(30)]),
        rulesText: new FormControl(cue5050?.receiptInfo?.rulesText ?? 'To claim winnings, you must present this original receipt for verification upon completion of the event. We reserve the right to reject a ticket receipt for any reason. Not responsible for lost, stolen, or damaged ticket receipts.', [Validators.required, Validators.minLength(15)]),
        footerHeader: new FormControl(cue5050?.receiptInfo?.footerHeader ?? 'HOW TO REDEEM', [Validators.required, Validators.minLength(1), Validators.maxLength(20)]),
        footerText: new FormControl(cue5050?.receiptInfo?.footerText ?? 'If you have the winning ticket, please proceed to the nearest 5050 kiosk to verify your ticket.', [Validators.required, Validators.minLength(15)])
      }),

      winnerSelectTime: new FormControl({ value: cue5050?.winnerSelectTime ? cue5050.winnerSelectTime : '12:00:00', disabled: this.eventId > 0 }, Validators.required),
      eventStartTime: new FormControl({ value: cue5050?.eventStartTime ? cue5050.eventStartTime : '12:00:00', disabled: this.eventId > 0 }, Validators.required),
      customWinnerSelectTime: new FormControl(cue5050?.customWinnerSelectTime ? cue5050.customWinnerSelectTime : ''),
      ticketNumberPrefix: [cue5050?.ticketNumberPrefix ?? '', [Validators.maxLength(4)]],
      ticketNumberSuffix: [cue5050?.ticketNumberSuffix ?? '', [Validators.maxLength(4)]],
      useRandomNumbers: [{ value: cue5050?.useRandomNumbers ?? false, disabled: this.eventId > 0 }, /*Validators.required*/],
      ticketIncrement: [{ value: cue5050?.ticketIncrement ?? 1, disabled: this.eventId > 0 }, [Validators.required, Validators.min(1), Validators.max(1600)]],
      startingTicketNumber: [{ value: cue5050?.startingTicketNumber ?? 1, disabled: this.eventId > 0 }, [Validators.required, Validators.min(1), Validators.max(16000)]],

      ticketsAvailable: [
        {value: cue5050?.ticketsAvailable ?? 10000, disabled: this.eventId > 0 },
        [Validators.required, Validators.min(100), ((): ValidatorFn => {
          return (control: AbstractControl): { [key: string]: any } | null => {
            let isValid = true;
            if (this.form && control.value !== null) {
              if (control.value > this.maxTicketNumber / this.form.controls.ticketIncrement.value - this.form.controls.startingTicketNumber.value) {
                isValid = false;
              }
            }
            return isValid
              ? null
              : { 'ticketsAvailable': { value: control.value } };
          };
        })()]],

      minimumDisplayValue: [{ value: (cue5050?.minimumDisplayValue ?? 0), disabled: this.eventId > 0 }, [Validators.min(0), Validators.max(100000)]],
      startingPotSeed: [{ value: (cue5050?.startingPotSeed ?? 0) / 100, disabled: this.eventId > 0 }, [Validators.min(0), Validators.max(100000)]],
      startingPotSeedDurationHours: [{
        value: cue5050 && cue5050.potSeedingDurationSeconds > 0 ? Math.floor((cue5050?.potSeedingDurationSeconds ?? 0) / 3600) : '',
        disabled: +cue5050?.startingPotSeed === 0 ?? this.eventId > 0 }],
      startingPotSeedDurationMinutes: [{
        value: cue5050 && cue5050.potSeedingDurationSeconds > 0 ? Math.floor(((cue5050?.potSeedingDurationSeconds ?? 0) % 3600) / 60) : '',
        disabled: +cue5050?.startingPotSeed === 0 ?? this.eventId > 0 }],
      percentWinner: [{ value: cue5050?.percentWinner ?? 50, disabled: this.eventId > 0 }, [Validators.required, Validators.min(0), Validators.max(60)]],
      expensePercent: [{ value: cue5050?.expensePercent ?? 10, disabled: this.eventId > 0 }, [Validators.required, Validators.min(0), Validators.max(20)]],
      expenseCap: [(cue5050?.expenseCap ?? 0) / 100, [Validators.min(0), Validators.max(100000000)]],

      pricingTiers: this.createPricingTierFormGroup(cue5050?.pricingTiers)


    });

    if (cue5050?.customWinnerSelectTime){
      this.showCustomizableWinnerText = true;
    } else {
      this.showCustomizableWinnerText = false;
    }

    if (cue5050?.event.status > 1 && !this.cloning){
      this.viewing = true;
      for (let control in this.form.controls){
        this.form.controls[control].disable();
      }
    }

    this.generateFirstFiveTickets();
    this.vendorName = this.getVendorName();
  }

  compare(val1, val2) {
    return val1 === val2;
  }

  getMaxTicketsAvailable() {
    return Math.floor(this.maxTicketNumber / +this.formControls('ticketIncrement').value) - +this.formControls('startingTicketNumber').value;
  }

  createLineItems(items: [{name: string, enabled: boolean}]) {

    const info = new FormArray<FormGroup>([]);

    if (items && items.length > 0){

      items.forEach(item => info.push(this.fb.group({ name: [item.name], enabled: [item.enabled]} )));

    } else {
        info.push(this.fb.group({ name: ["DATE"], enabled: [true] })),
        info.push(this.fb.group({ name: ["TIME"], enabled: [true] })),
        info.push(this.fb.group({ name: ["FIRST"], enabled: [false] })),
        info.push(this.fb.group({ name: ["LAST"], enabled: [false] })),
        info.push(this.fb.group({ name: ["DOB"], enabled: [false] })),
        info.push(this.fb.group({ name: ["EMAIL"], enabled: [false] })),
        info.push(this.fb.group({ name: ["PHONE"], enabled: [false] }))
    }

    info.setValidators([Validators.maxLength(18)]);

    return info;
  }

  ignoreWhiteSpace(t: any) {
    t.controls.amount.patchValue(t.controls.amount.value.replace(/\s/g, ''));
    t.controls.amount.updateValueAndValidity();
  }

  getEndDateTime() {
    const t = (!this.eventFormControls('realEventPeriod')?.invalid ? formatDateTime(this.eventFormControls('realEventPeriod')?.value.endDate) : new Date())
      + 'T' + (!this.formControls('winnerSelectTime').invalid ? (this.formControls('winnerSelectTime').value) : '12:00:00')  ;
    return t;
  }

  getStartDateTime() {
    const t = (!this.eventFormControls('realEventPeriod')?.invalid ? formatDateTime(this.eventFormControls('realEventPeriod')?.value.startDate) : new Date())
      + 'T' + (!this.formControls('eventStartTime').invalid ? (this.formControls('eventStartTime').value) : '12:00:00')  ;
    return t;
  }

  determineWinnerSelectionText(customText: boolean){
    return customText ? this.formControls('customWinnerSelectTime').value :
    `A winner will be announced at ${ this.datePipe.transform(this.getEndDateTime(), 'h:mm a')  } on ${ this.datePipe.transform(this.getEndDateTime(), 'MM/dd/yy') }.`
  }

  removeLeadingZeros(control: AbstractControl) {
    control.patchValue(control.value?.replace(/^0+/, ''));
  }

  getVendorName() {
    return localStorage.getItem('vendorName');
  }

  save() {
    this.hasValidationErrors = !this.form.valid;
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
        if (controls[name].invalid) {
            invalid.push(name);
        }
    }
    if (this.hasValidationErrors) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.spinner.show();

    const cue5050Data = new Cue5050(this.form.getRawValue());
    cue5050Data.event.realStartDateTime = this.eventFormControls('realEventPeriod')?.value.startDate.toJSON();
    cue5050Data.event.realEndDateTime = this.eventFormControls('realEventPeriod')?.value.endDate.toJSON();
    // cue5050Data.potSeedingDurationSeconds =
    //   this.formControls('startingPotSeedDurationHours').value * 3600 + this.formControls('startingPotSeedDurationMinutes').value * 60;
    cue5050Data.potSeedingDurationSeconds = 0;

    cue5050Data.pricingTiers.map(t => t.price *= 100);
    cue5050Data.expenseCap *= 100;
    //cue5050Data.startingPotSeed *= 100;
    cue5050Data.startingPotSeed = 0;
    cue5050Data.event.fundraisingGoal *= 100;

    if(this.eventId > 0) {
      this.cue5050Service.update(cue5050Data).subscribe(
        () => {
          this.spinner.hide();
          this.navigateUp();
        },
        error => {
          this.spinner.hide();
          this.dialogService.error(error, "Unable to update cue5050", this.viewRef);
        }
      );
    } else {
      this.cue5050Service.create(cue5050Data).subscribe(
        () => {
          this.spinner.hide();
          this.router.navigate([`/portal/fundraising/cue5050/event`])
        },
        error => {
          this.spinner.hide();
          this.dialogService.error(error, "Unable to create cue5050", this.viewRef);
        }
      );
    }
  }

  newPricingTier() {
    (<FormArray>this.form.controls.pricingTiers)
      .push(
        this.fb.group({
          description: [null, Validators.required],
          amount: [null, Validators.required],
          price: [null, Validators.required]
        })
      );
    this.form.controls.pricingTiers.markAsDirty();
    this.form.updateValueAndValidity();
  }

  createPricingTierFormGroup(tiers: PricingTier[]) {

    const f = new FormArray<FormGroup>([]);

    if (tiers && tiers.length > 0) {
      tiers.forEach(q =>
        f.push(this.fb.group({
          description: [q.description, Validators.required],
          amount: [+q.amount, Validators.required],
          price: [+q.price / 100, Validators.required]
        }))
      );
    } else {
      f.push(this.fb.group({
        description: ['Basic', Validators.required],
        amount: [1, Validators.required],
        price: [10, Validators.required]
      }));
    }

    return f;
  }

  deletePricingTier(tierIndex: number) {
    (<FormArray>this.form.controls.pricingTiers).removeAt(tierIndex);
    this.form.controls.pricingTiers.markAsDirty();
    this.form.updateValueAndValidity();
  }

  generateFirstFiveTickets() {

    if (this.formControls('useRandomNumbers').value) {
      this.formControls('startingTicketNumber').patchValue(1);
      this.formControls('ticketIncrement').patchValue(1);
      this.formControls('startingTicketNumber').setValidators(null);
      this.formControls('startingTicketNumber').setErrors(null);
      this.formControls('ticketIncrement').setValidators(null);
      this.formControls('ticketIncrement').setErrors(null);
    } else {
      this.formControls('startingTicketNumber').setValidators([Validators.required, Validators.min(1), Validators.max(16000)]);
      this.formControls('ticketIncrement').setValidators([Validators.required, Validators.min(1), Validators.max(1600)]);
      this.formControls('startingTicketNumber').markAsTouched();
      this.formControls('ticketIncrement').markAsTouched();
    }

    this.form.updateValueAndValidity();

    if (!this.formControls('ticketNumberSuffix').valid ||
      !this.formControls('ticketNumberPrefix').valid ||
      !this.formControls('ticketsAvailable').valid ||
      !this.formControls('startingTicketNumber').valid ||
      !this.formControls('ticketIncrement').valid) {
      return;
    }

    this.firstFiveTickets = [];

    const ticketsAvailable = this.formControls('ticketsAvailable').value;

    for (let i = 0; i < 5; i++) {
      if (this.formControls('useRandomNumbers').value) {
        this.firstFiveTickets.push(
          this.formControls('ticketNumberPrefix').value +
          Math.floor(Math.random() * ticketsAvailable).toString().padStart(ticketsAvailable.toString().length, '0') +
          this.formControls('ticketNumberSuffix').value);
      } else {
        this.firstFiveTickets.push(this.formControls('ticketNumberPrefix').value +
        String(i * this.formControls('ticketIncrement').value + this.formControls('startingTicketNumber').value)
          .padStart(ticketsAvailable.toString().length, '0') +
        this.formControls('ticketNumberSuffix').value)
      }
    }
  }


  // Alex: This is not correct, see GetPrice on the backend
  calculatePercentBreakdown(){
    const percentages = {
      percentToVendor: +((this.form.controls.expensePercent.value / 100) * 100).toFixed(2),
      processingFees: {
        online: +((2.9 /100 * 100) + 0.3).toFixed(2),
        onsite: +((2.7 / 100 * 100) + 0.05).toFixed(2)
      },
      administrationFees: +(this.defaultAdminPercent / 100 * 100).toFixed(2),
      netAmount: 0,
      percentToWinner: 0,
      percentToCharity: 0,
    }
    percentages.netAmount = +(100 - percentages.processingFees.online - percentages.administrationFees - percentages.percentToVendor).toFixed(2);
    percentages.percentToWinner = +(percentages.netAmount * this.form.controls.percentWinner.value/100).toFixed(2);
    percentages.percentToCharity = +(percentages.netAmount - percentages.percentToWinner);

    return percentages;
  }

  public navigateUp() {
    this.router.navigate([this.eventId > 0 || this.cloning ? '../../' : '../'], {relativeTo: this.route});
  }

}
